<template>
  <div class="container_CargasTable">
    <PCard>
      <div class="actions_cargas">
        <PSidebar
          v-model="mostrarFiltros"
          :title="$t('label.filtros')"
          icon="IconFilter"
          bVariant="secondary"
          :textButton="dameTextoBotonFiltros"
        >
          <CargaFilterForm
            v-model="filtersCargas"
            actionName="filtrar"
            :textActionName="$t('label.filtrar')"
            :optionsCertificados="certificados"
            :optionEstados="estados"
            @action="actions"
            @cancel="actions"
          />
        </PSidebar>
      </div>
      <div>
        <span class="float-left mr-3">
          <b-button
            id="button-selectAll"
            class="p-button-secondary pt-1"
            variant="outline-secondary"
            v-b-tooltip.hover.d300="$t('action.selectAll')"
            @click="selectAllCerts"
          >
            <b-form-checkbox v-model="checkedAll" @change="selectAllCerts">
              {{ checkedAll ? $t("label.deselectAll") : $t("label.selectAll") }}
            </b-form-checkbox>
          </b-button>
        </span>
        <span class="float-left">
          <b-button
            :disabled="selectAllLength === 0"
            class="p-button-primary"
            id="action-remove"
            @click.prevent="removeCertificado()"
          >
            <icon-trash class="icon-trash" :size="16" color="#000" />
            {{ $t("action.remove") }}
            <b-tooltip target="action-remove" placement="top">
              {{ $t("action.deleteSelect") }}
            </b-tooltip>
          </b-button>
        </span>
      </div>
      <div class="table-responsive">
        <b-table
          :fields="visibleFields"
          :items="cargas"
          :sort-by.sync="pagination.orderBy"
          :sort-desc.sync="sortDesc"
          sort-icon-left
          no-local-sorting
          :empty-text="$t('label.noData')"
          ref="tableCargas"
          show-empty
          no-border-collapse
          borderless
          responsive
          @sort-changed="onSortingChange"
        >
          <div class="text-center loader my-2" slot="table-busy">
            <b-spinner class="align-middle" />
          </div>
          <template #cell(actions-left)="row">
            <div class="table-button-container d-flex">
              <span
                :id="`removeCert-${row.index}`"
                class="table-button-container pointer removeCert mx-1 my-auto"
              >
                <b-form-checkbox
                  :id="`checkbox-${row.index}`"
                  v-model="row.item.checked"
                  @change="selectOne(row.index)"
                >
                </b-form-checkbox>
                <b-tooltip
                  :target="`removeCert-${row.index}`"
                  placement="lefttop"
                  v-if="showTooltip[row.index]"
                >
                  {{ $t("action.select") }}
                </b-tooltip>
              </span>
            </div>
          </template>
          <template #head(actions-right)>
            <select-columns :fields.sync="fields" :pagination="pagination" :cod-table="codTable" />
          </template>
        </b-table>
      </div>
      <div class="mt-3" v-if="totalElements > pagination.size">
        <pagination
          :current-page="pagination.page"
          :page-size="pagination.size"
          :total-elements="totalElements"
          @change="onChangeCurrentPage"
        />
      </div>
    </PCard>
  </div>
</template>

<script lang="ts">
import Vue from "vue";
import { Component, Prop } from "vue-property-decorator";
import { ICarga, IConfColumnaPais, IField, IParametro, ISoConfColumnasUpdateDTO } from "@/entities";
import {
  EDirection,
  ICargaFilter,
  ICargaParams,
  ICargaResponse,
  IColumnaResponse,
  IPaged,
} from "@/services";
import VueFilters from "@/vue-filters";
import Pagination from "@/components/common/Pagination.vue";
import { Action } from "vuex-class";
import SelectColumns from "@/components/common/SelectColumns/SelectColumns.vue";
import { SweetAlertResult } from "sweetalert2";
import CargaFilterForm from "@/components/administracion/cargas/CargaFilterForm.vue";
import { Dictionary } from "vue-router/types/router";
import VueMethods from "@/vue-methods";

@Component({
  components: { CargaFilterForm, SelectColumns, Pagination },
  mixins: [VueFilters, VueMethods],
})
export class CargaTable extends Vue {
  @Prop({ required: true }) columnsConfig!: IColumnaResponse;
  @Prop({ required: true }) params!: ICargaParams;
  @Action("fetchCarga")
  fetchCarga!: (params?: ICargaParams) => Promise<ICargaResponse | undefined>;
  @Action("saveColumna") saveColumna!: (
    params: ISoConfColumnasUpdateDTO
  ) => Promise<IColumnaResponse | undefined>;

  public pagination: IPaged = {
    direction: EDirection.DESC,
    orderBy: "id",
    page: 1,
    size: 10,
  };

  public filtersCargas: ICargaFilter = {
    codFilename: "",
    fecCarga: "",
    estadoSelected: "",
    paises: [],
    codCliente: "",
    codDelegacion: "",
    fecFichero: "",
    fechaNormalizacion: "",
    tipoCertificado: "",
    codigoCertificado: "",
  };

  public certificados: { key: string; value: string }[] = [
    { key: "DE", value: "DE" },
    { key: "PC", value: "PC" },
  ];

  mostrarFiltros = false;
  codTable = "cargas";
  private sortDesc = true;
  estados: IParametro[] = [];
  cargas: ICarga[] = [];
  totalElements = 0;
  showTooltip: boolean[] = [];

  beforeMount(): void {
    this.filtersCargas.codFilename = this.params.codFilename ?? "";
    this.filtersCargas.fecCarga = this.params.fecCarga ?? "";
    this.filtersCargas.estadoSelected = this.params.estado ?? "";
    this.filtersCargas.paises = this.params.paises ?? [];
    this.filtersCargas.codCliente = this.params.codCliente ?? "";
    this.filtersCargas.codDelegacion = this.params.codDelegacion ?? "";
    this.filtersCargas.fecFichero = this.params.fecFichero ?? "";
    this.filtersCargas.fechaNormalizacion = this.params.fechaNormalizacion || "";
    this.filtersCargas.tipoCertificado = this.params.tipoCertificado ?? "";
    this.filtersCargas.codigoCertificado = this.params.codigoCertificado ?? "";
  }

  async mounted(): Promise<void> {
    await this.getEstados();
    await this.getCargas();
  }

  async getEstados(): Promise<void> {
    this.estados = (await this.$services.carga.fetchEstadosCarga()) || [];
  }
  async search(): Promise<void> {
    this.pagination.page = 1;
    await this.getCargas();
  }

  get dameTextoBotonFiltros(): string {
    let numFiltros = 0;
    Object.keys(this.filtersCargas).forEach((key: string) => {
      if (this.filtersCargas[key] != "" && this.filtersCargas[key] != null) {
        numFiltros = numFiltros + 1;
      }
    });

    return numFiltros ? `(${numFiltros}) ${this.$t("label.filtros")}` : this.$t("label.filtros");
  }

  get fields(): IField[] {
    const visibleKeys = this.columnsConfig?.codColumna?.split(",").filter((s) => s !== "") || [];
    let columnas: IField[] = [];
    this.columnsConfig.columnas?.forEach((column: IConfColumnaPais) => {
      columnas.push({
        key: "actions-left",
        label: "",
        sortable: false,
        thClass: "table-header table-header-add-columnas pb-0",
        tdClass: "table-cell-middle actions",
        visible: true,
        class: "table-cell",
        aux: true,
        code: "",
      });
      columnas.push({
        key: column.keyFront,
        label: this.$t("label." + column.codColumna),
        sortable: true,
        thClass: "table-header",
        tdClass: "table-cell-middle",
        thStyle: column.widthFront ? { width: column.widthFront } : {},
        visible: this.isVisible(visibleKeys, column.codColumna),
        class: "table-cell",
        aux: false,
        code: column.codColumna,
        formatter: (value: any): string => {
          if (column.formatType == "Date") {
            return this.filterDate(value);
          } else if (column.formatType == "Number") {
            return this.filterNumber(value, 2);
          }
          return value;
        },
      });
    });
    columnas.push({
      key: "actions-right",
      label: "",
      sortable: false,
      thClass: "table-header table-header-add-columnas pb-0",
      tdClass: "table-cell-middle actions",
      visible: true,
      class: "table-cell",
      aux: true,
      code: "",
    });
    return columnas;
  }

  set fields(fields: IField[]) {
    this.columnsConfig.codColumna =
      fields
        .filter((item) => item.visible && !item.aux)
        .map((item) => item.code)
        .join(",") || "";
  }

  isVisible(visibleKeys: string[], column: string): boolean {
    return !visibleKeys.length || !!visibleKeys.find((key) => key === column);
  }

  get visibleFields(): IField[] {
    return this.fields.filter((item) => item.visible);
  }

  async getCargas(): Promise<void> {
    const params: ICargaParams = { ...this.params };
    params.page = this.pagination.page! - 1;
    params.size = this.pagination.size;
    params.orderBy = this.pagination.orderBy;
    params.direction = this.pagination.direction;

    const response: ICargaResponse | undefined = await this.$services.carga.fetch(params);

    if (response) {
      this.cargas = response.content;
      this.totalElements = response.totalElements;
      this.showTooltip = Array(this.cargas.length).fill(true);
    }
  }

  onSortingChange(): void {
    this.$nextTick(async () => {
      this.pagination.direction = this.sortDesc ? EDirection.DESC : EDirection.ASC;
      await this.getCargas();
      await this.saveColumnsConfig();
    });
  }

  onChangeCurrentPage(currentPage: number): void {
    this.pagination.page = currentPage;
    this.getCargas();
  }

  async saveColumnsConfig(): Promise<void> {
    const params: ISoConfColumnasUpdateDTO = {
      codColumna: this.columnsConfig.codColumna || "",
      codTabla: this.columnsConfig.codTabla,
    };
    if (this.pagination) {
      params.codTipoOrden = this.pagination.direction;
      params.numPagElements = this.pagination.size;
      params.codOrden = this.pagination.orderBy;
    }
    await this.saveColumna(params);
  }

  get selectAllLength(): number {
    return this.cargas.filter((carga) => carga.checked).length;
  }

  get checkedAll(): boolean {
    return this.cargas.every((carga) => carga.checked);
  }

  set checkedAll(_checked: boolean) {
    //Do nothing
  }

  selectOne(index: number): void {
    this.$set(this.showTooltip, index, false);
    setTimeout(() => {
      this.$set(this.showTooltip, index, true);
    }, 1000);
  }

  selectAllCerts(): void {
    const checked = !this.checkedAll;
    this.cargas.forEach((carga) => this.$set(carga, "checked", checked));
  }

  async modalConfirm(): Promise<SweetAlertResult> {
    return this.$swal({
      title: this.$t("message.selectDelete.confirm.title", [this.selectAllLength]),
      icon: "question",
      showCancelButton: true,
      confirmButtonText: this.$t("label.yes"),
      cancelButtonText: this.$t("label.no"),
    });
  }

  async removeCertificado(): Promise<void> {
    const { isConfirmed } = await this.modalConfirm();
    if (isConfirmed) {
      let cargasDelete: ICarga[] = [];

      this.cargas.forEach((carga) => {
        if (carga.checked) {
          if (carga.codCliente && carga.id) {
            cargasDelete.push(carga);
          }
        }
      });
      await this.$services.carga.removeAll(cargasDelete);
      this.makeToast("success", this.$t("validation.messages.cert.ok"));
      await this.getCargas();
    }
  }

  actions(obj: { action: string; data: ICargaFilter }): void {
    if (obj.action === "filtrar" || obj.action === "clean") {
      this.params.fecCarga = this.filtersCargas.fecCarga
        ? this.getStartDate(this.filtersCargas.fecCarga)
        : this.getThreeDate(new Date().toISOString());
      this.params.codFilename = this.filtersCargas.codFilename ?? undefined;
      this.params.estado = this.filtersCargas.estadoSelected ?? undefined;
      this.params.paises = this.filtersCargas.paises ?? undefined;
      this.params.codCliente = this.filtersCargas.codCliente ?? undefined;
      this.params.codDelegacion = this.filtersCargas.codDelegacion ?? undefined;
      this.params.fecFichero = this.filtersCargas.fecFichero
        ? this.getStartDate(this.filtersCargas.fecFichero)
        : undefined;
      this.params.fechaNormalizacion = this.filtersCargas.fechaNormalizacion
        ? this.getStartDate(this.filtersCargas.fechaNormalizacion)
        : undefined;
      this.params.tipoCertificado = this.filtersCargas.tipoCertificado ?? undefined;
      this.params.codigoCertificado = this.filtersCargas.codigoCertificado ?? undefined;

      this.replaceQueryParams();
      this.search();

      this.mostrarFiltros = false;
    }
  }

  private replaceQueryParams(): void {
    this.$router
      .replace({
        path: this.$route.path,
        query: { ...this.params } as Dictionary<any>,
      })
      .catch(() => {
        // Do nothing
      });
  }

  makeToast(variant: string, message: string): void {
    this.$bvToast.toast(message, {
      autoHideDelay: 4000,
      variant,
      toaster: "b-toaster-bottom-center",
      noCloseButton: true,
    });
  }
}
export default CargaTable;
</script>
<style lang="scss" scoped>
.actions_cargas {
  display: flex;
  flex-wrap: wrap;
  justify-content: flex-end;

  @media (min-width: 681px) {
    float: right;
  }
}
.container_CargasTable {
  margin-top: 2rem;
}
.removeCert {
  cursor: pointer;
  margin-top: -8px !important;
}
#button-selectAll::v-deep .custom-control-label {
  font-size: 14px;
  font-weight: 400;

  &::before,
  &::after {
    top: 0.3rem;
    line-height: 1.3;
  }
}
</style>
